import {useMemo} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {ProcessFlowchartStructureQuery} from './__generated/ProcessFlowchartStructureQuery';
import {ProcessTaskGroupFlowchartDataQuery} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import buildDisplayOnlyDraftFromStructure from './buildDisplayOnlyDraftFromStructure';
import type {ViewerStaticTemplateFlowchart} from './ReadOnlyFlowchartTypes';
import {getTaskGroupLineageSummary} from './util/mergeTaskGroupLineageIntoFlowchart';

export type LineageFlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;
export type LineageFlowchartProcess = ExtractByTypename<
  ProcessTaskGroupFlowchartDataQuery['process'],
  'ProcessNode'
>;

export interface LineageFlowchartData {
  displayOnlyFlowchart: ViewerStaticTemplateFlowchart;
  processIdsByNodeConfigId: Record<string, string>;
  lineageEdgeHashes: ReadonlySet<string>;
  /** Populated from `flowchartNodeUrl` on the GQL process fragment. Present when any
   *  process node has a URL (e.g. procedure-run task details page). Undefined otherwise. */
  nodeUrlsByNodeConfigId: Record<string, string> | undefined;
}

/**
 * Derives the display-only flowchart, lineage edge hashes, process ID map, and
 * node URL map from already-fetched GQL structure and process data.
 *
 * Used by both the task-group and task-details lineage viewers so the
 * "Show nodes without tasks" toggle works on both pages.
 */
export function useLineageFlowchartData(
  flowchartStructure: LineageFlowchartStructure,
  flowchartProcess: LineageFlowchartProcess
): LineageFlowchartData {
  const {flowchart: displayOnlyFlowchart, processIdsByNodeConfigId} = useMemo(
    () => buildDisplayOnlyDraftFromStructure(flowchartStructure, flowchartProcess),
    [flowchartStructure, flowchartProcess]
  );

  const lineageEdgeHashes = useMemo(() => {
    const minimalFlowchart = {
      nodes: flowchartStructure.nodeConfigs.map((nc) => ({
        type: nc.nodeDetails.type,
        clientId: nc.id,
      })),
    };
    return getTaskGroupLineageSummary(minimalFlowchart, flowchartProcess).edgeHashes;
  }, [flowchartStructure, flowchartProcess]);

  const nodeUrlsByNodeConfigId = useMemo(() => {
    const urls: Record<string, string> = {};
    for (const process of [flowchartProcess, ...flowchartProcess.childProcesses]) {
      if (isInaccessible(process) || process.nodeConfig == null || process.flowchartNodeUrl == null) {
        continue;
      }
      urls[process.nodeConfig.id] = process.flowchartNodeUrl;
    }
    return Object.keys(urls).length > 0 ? urls : undefined;
  }, [flowchartProcess]);

  return {displayOnlyFlowchart, processIdsByNodeConfigId, lineageEdgeHashes, nodeUrlsByNodeConfigId};
}
